// @ts-nocheck

import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { BetCategoriesForMatchFilters } from '../../constants/bet';
import { __ } from '../../i18n/l10n';
import { selectEntitiesMatches } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { MatchEntity } from '../../states/entities/matches';
import { TournamentState } from '../../states/entities/tournaments';
import theme from '../../styled-components/dark';
import type { SearchResultsType } from '../../types/search';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import FlagIcon from '../FlagIcon';
import Link from '../Link';
import ListItem from '../ListItem';
import CategoryRowSearchResult from '../SearchCategoryRowSearchResult';
import DefaultSearchRowResult from '../SearchDefaultSearchRowResult';
import SearchDefaultSearchRowResultContainer from '../SearchDefaultSearchRowResult/SearchDefaultSearchRowResultContainer';
import SportIcon from '../SportIcon';
import WinaLoader from '../WinaLoader';

const defaultResults: SearchResultsType = {
  matches: [],
  tournaments: [],
  bets: [],
  outcomes: [],
};

const MAX_WRAPPER_HEIGHT = 500;
const PADDING_BOTTOM = 50;

const BORDER_RADIUS = 15; // px

type WrapperPropsType = {
  $afterOpacity: number;
  $isScrollable: boolean;
};
const Wrapper = styled.div`
  width: 100%;
  border-radius: 5px;

  ${!Target.__MOBILE__
    ? css<WrapperPropsType>`
        ${({ $isScrollable = true }) =>
          $isScrollable
            ? css`
                overflow-y: scroll;
                max-height: ${MAX_WRAPPER_HEIGHT}px;
              `
            : undefined}

        &:after {
          content: '';
          position: absolute;
          top: calc(99.5% - ${PADDING_BOTTOM}px);
          left: 0;
          height: ${PADDING_BOTTOM}px;
          width: 100%;
          transition: opacity 300ms ease-in-out;
          background: linear-gradient(0deg, black, #000000d7 30%, transparent);
          opacity: ${({ $afterOpacity }) => $afterOpacity};
          pointer-events: none;
        }
      `
    : undefined}
`;

const FullCenterWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const CategoryHeader = styled.div`
  padding: 1em 0.5em 0.4em 0.5em;
  text-transform: capitalize;
  font-weight: bold;
  font-size: 115%;
  color: ${theme.SearchResults.categoriesColor};
`;

const StyledLink = styled(Link)`
  &:first-child {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }
  &:last-child {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  padding: 0em;
  padding-left: 0em;
  & > * {
    padding: 0;
    padding-left: 0;
  }
  & * {
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }
`;

const SharedGroupStyle = css`
  border-radius: ${BORDER_RADIUS}px;
  background-color: ${theme.SearchResults.categoriesBackgroundColor};
`;

const Category = styled.div``;

const Matches = styled.div`
  ${SharedGroupStyle}
`;

const Categories = styled.div`
  ${SharedGroupStyle}
`;

const StyledListItem = styled(ListItem)`
  background-image: none;
`;

const StyledWinaLoader = styled(WinaLoader)`
  ${!Target.__MOBILE__
    ? css`
        margin: 5em 0em;
      `
    : undefined}
`;

const NoResultWrapper = styled.div`
  padding: 1em 0;
`;

type SearchResultsCompetitionsPropsType = {
  tournaments: Array<Partial<TournamentState> & { sportId: number; categoryName?: string; categoryFlag?: string }>;
  highlightIndex: number;
  onClick: (e?: React.MouseEvent) => void;
};
const SearchResultsCompetitions = ({ tournaments, highlightIndex, onClick }: SearchResultsCompetitionsPropsType) => {
  return (
    <Category>
      <CategoryHeader>{__('Competitions')}</CategoryHeader>
      <Categories>
        {[
          ...tournaments.map((tournament, i) => {
            const { tournamentId, categoryId, sportId, tournamentName, categoryName, flag, categoryFlag } = tournament;
            const label = `${categoryName ?? ''} - ${tournamentName ?? ''}`;
            const to = `/sports/${sportId}/${categoryId}/${tournamentId}`;
            const icons = [];
            if (sportId) {
              icons.push(<SportIcon key={`sport_${sportId}`} sportId={sportId} />);
            }
            if (flag) {
              icons.push(<FlagIcon key={`flag_${flag}`} flagId={flag} />);
            }
            if (categoryFlag && !flag) {
              icons.push(<FlagIcon key={`category_${categoryFlag}`} flagId={categoryFlag} />);
            }
            const currIndex = i;
            const isHighlighted = currIndex === highlightIndex;

            return (
              <StyledLink key={`tournamentSearchResultRow-${tournamentId}`} to={to}>
                <StyledListItem onClick={onClick} odd={currIndex % 2 !== 0} isHighlighted={isHighlighted}>
                  <CategoryRowSearchResult label={label} icons={icons} border={i !== 0} />
                </StyledListItem>
              </StyledLink>
            );
          }),
        ]}
      </Categories>
    </Category>
  );
};

const SearchResultsBets = ({
  entitiesMatches,
  nbOutcomes,
  nbBets,
  outcomes,
  bets,
  nbMatches,
  nbTournaments,
  highlightIndex,
  onClick,
}) => {
  return (
    <Category>
      <CategoryHeader>{__('!short:bet', nbOutcomes + nbBets, '!short:bets')}</CategoryHeader>
      <Matches>
        {outcomes.map((outcome, i) => {
          const {
            matchId,
            betTitle,
            matchStart,
            sportId,
            matchTitle,
            outcomeId,
            outcomeTitle,
            betCategoryId,
            playerId,
          } = outcome;

          const routeHasOutcomeId = BetCategoriesForMatchFilters.indexOf(betCategoryId) === -1; // Why we're doing so => https://github.com/wmx-betting/winabet/issues/2915
          const matchRoute = RoutesRedirectionFactory.getMatchRoutePathname(matchId);

          let to = '';

          if (matchRoute) {
            to += matchRoute;

            const contextedParams = [
              { shortname: 'o', value: routeHasOutcomeId ? outcomeId : undefined },
              { shortname: 'bc', value: betCategoryId },
              { shortname: 'p', value: playerId },
            ].filter((param) => !!param.value);

            for (let i = 0; i < contextedParams.length; i++) {
              const { shortname, value } = contextedParams[i];
              const sep = i === 0 ? '#' : '&';

              if (value) {
                to += `${sep}${shortname}=${value}`;
              }
            }
          }

          const currIndex = i + nbMatches + nbTournaments;
          const isHighlighted = currIndex === highlightIndex;
          const match = entitiesMatches?.[matchId];
          const isLive = MatchEntity.isLive(match);

          return (
            <StyledLink key={to} to={to}>
              <StyledListItem onClick={onClick} odd={currIndex % 2 !== 0} isHighlighted={isHighlighted}>
                <DefaultSearchRowResult
                  sportId={sportId}
                  title={betTitle}
                  secondTitle={outcomeTitle}
                  startDate={matchStart}
                  subtitleNames={[matchTitle]}
                  isLive={isLive}
                  border={i !== 0}
                />
              </StyledListItem>
            </StyledLink>
          );
        })}
        {bets.map((bet, i) => {
          const { matchId, betId, matchStart, betTitle, sportId, matchTitle, betCategoryId } = bet;
          // eslint-disable-next-line sonarjs/no-nested-template-literals
          const to = `/match/${matchId}#b=${betId}${typeof betCategoryId === 'number' ? `&bc=${betCategoryId}` : ''}`;
          const currIndex = i + nbMatches + nbTournaments + nbOutcomes;
          const isHighlighted = currIndex === highlightIndex;

          return (
            <StyledLink key={to} $localIndex={i + nbOutcomes} to={to}>
              <StyledListItem onClick={onClick} odd={currIndex % 2 !== 0} isHighlighted={isHighlighted}>
                <DefaultSearchRowResult
                  sportId={sportId}
                  title={betTitle}
                  startDate={matchStart}
                  subtitleNames={[matchTitle]}
                  border={i + nbOutcomes !== 0}
                />
              </StyledListItem>
            </StyledLink>
          );
        })}
      </Matches>
    </Category>
  );
};

const SearchResultsMatches = ({ matches, nbTournaments, highlightIndex, onClick }) => {
  return (
    <Category>
      <CategoryHeader>{__('Matches')}</CategoryHeader>
      <Matches>
        {matches.map((match, i) => {
          const { matchId, sportId, title, matchStart, categoryId, tournamentId } = match;
          const to = `/match/${matchId}`;
          const currIndex = i + nbTournaments;
          const isHighlighted = currIndex === highlightIndex;
          const isLive = MatchEntity.isLive(match);

          return (
            <StyledLink key={`matchesSearchResultRow-${matchId}`} to={to}>
              <StyledListItem onClick={onClick} odd={currIndex % 2 !== 0} isHighlighted={isHighlighted}>
                <SearchDefaultSearchRowResultContainer
                  sportId={sportId}
                  title={title}
                  startDate={matchStart}
                  categoryId={categoryId}
                  tournamentId={tournamentId}
                  isLive={isLive}
                  border={i !== 0}
                />
              </StyledListItem>
            </StyledLink>
          );
        })}
      </Matches>
    </Category>
  );
};

type PropsType = {
  results: SearchResultsType;
  isLoading: boolean;
  clearInput?: () => void;
  highlightIndex?: number;
  className?: string;
};

const SearchResults = ({
  isLoading = false,
  results = defaultResults,
  clearInput = () => {},
  highlightIndex,
  className,
}: PropsType) => {
  const wrapperRef = useRef();
  const [wrapperAfterOpacity, setWrapperAfterOpacity] = useState(1);
  const [isWrapperScrollable, setIsWrapperScrollable] = useState(true);

  const bets = useMemo(() => results?.bets ?? [], [results]);
  const nbBets = useMemo(() => bets.length, [bets]);
  const hasBets = useMemo(() => !!nbBets, [nbBets]);

  const outcomes = useMemo(() => results?.outcomes ?? [], [results]);
  const nbOutcomes = useMemo(() => outcomes.length, [outcomes]);
  const hasOutcomes = useMemo(() => !!nbOutcomes, [nbOutcomes]);

  const entitiesMatches = useSelector(selectEntitiesMatches);
  const matches = useMemo(() => results?.matches ?? [], [results]);
  const nbMatches = useMemo(() => matches.length, [matches]);
  const hasMatches = useMemo(() => !!nbMatches, [nbMatches]);

  const tournaments = useMemo(() => results?.tournaments ?? [], [results]);
  const nbTournaments = useMemo(() => tournaments.length, [tournaments]);
  const hasTournaments = useMemo(() => !!nbTournaments, [nbTournaments]);

  const hasCompetitions = useMemo(() => hasTournaments, [hasTournaments]);

  const hasResult = useMemo(
    () => hasMatches || hasTournaments || hasBets || hasOutcomes,
    [hasMatches, hasTournaments, hasBets, hasOutcomes],
  );

  useEffect(() => {
    if (!Target.__MOBILE__ && !isLoading) {
      const node = wrapperRef?.current;

      if (node) {
        const nodeClientHeight = node.clientHeight;

        if (nodeClientHeight < MAX_WRAPPER_HEIGHT) {
          setIsWrapperScrollable(false);
          setWrapperAfterOpacity(0);
        } else {
          setIsWrapperScrollable(true);
          setWrapperAfterOpacity(1);
          const nodeScrollHeight = node.scrollHeight;

          if (nodeScrollHeight !== undefined && nodeClientHeight !== undefined) {
            const scrollTopMax = node.scrollHeight - node.clientHeight;

            const listener = () => {
              if (node.scrollTop > scrollTopMax - 60) {
                setWrapperAfterOpacity(0);
              } else {
                setWrapperAfterOpacity(1);
              }
            };
            node.addEventListener('scroll', listener);

            return () => {
              node.removeEventListener('scroll', listener);
            };
          }
        }
      }
    }
  }, [isLoading]);

  const onClick = useCallback(
    (e?: MouseEvent) => {
      if (!e?.metaKey && !e?.ctrlKey) {
        clearInput();
      }
    },
    [clearInput],
  );

  if (isLoading) {
    return (
      <FullCenterWrapper className={className}>
        <StyledWinaLoader />
      </FullCenterWrapper>
    );
  }

  if (hasResult) {
    return (
      <Wrapper
        id="scrollable-search-results"
        ref={wrapperRef}
        className={className}
        $afterOpacity={wrapperAfterOpacity}
        $isScrollable={isWrapperScrollable}
      >
        {hasCompetitions ? (
          <SearchResultsCompetitions
            tournaments={tournaments}
            nbTournaments={nbTournaments}
            highlightIndex={highlightIndex}
            onClick={onClick}
          />
        ) : null}

        {hasMatches ? (
          <SearchResultsMatches
            matches={matches}
            nbTournaments={nbTournaments}
            highlightIndex={highlightIndex}
            onClick={onClick}
          />
        ) : null}

        {hasBets || hasOutcomes ? (
          <SearchResultsBets
            entitiesMatches={entitiesMatches}
            onClick={onClick}
            nbOutcomes={nbOutcomes}
            nbBets={nbBets}
            outcomes={outcomes}
            bets={bets}
            nbMatches={nbMatches}
            nbTournaments={nbTournaments}
            highlightIndex={highlightIndex}
          />
        ) : null}
      </Wrapper>
    );
  }

  return (
    <FullCenterWrapper className={className}>
      <NoResultWrapper>{__('No results')}</NoResultWrapper>
    </FullCenterWrapper>
  );
};

export default SearchResults;
